import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { MetricCard, MetricLabel, MetricValue } from '../../../ui/metric-card';
import { EmissionBar } from '../../../ui/emission-bar';
import { LOCATION_BREAKDOWN, COMMUTING_MODE_SPLIT, sumCampaignTotals, totalTco2e } from '../../fixtures';
import { FileDown, Download, Leaf, Flame, Car, Zap, Snowflake } from 'lucide-react';

const ResultsTab = ({ campaign }) => {
  const totals = sumCampaignTotals();
  const totalAll = Object.values(totals).reduce((a, b) => a + b, 0);
  const maxCommuteTco2e = Math.max(...COMMUTING_MODE_SPLIT.map((r) => r.tco2e));

  const extrapolatedMultiplier = 1.66;
  const totalExtrapolated = Number((totalAll * extrapolatedMultiplier).toFixed(1));

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
        <MetricCard accent='success'>
          <MetricLabel>Respondent total</MetricLabel>
          <MetricValue unit='tCO2e'>{totalAll.toFixed(1)}</MetricValue>
          <div className='text-xs text-muted-foreground mt-2'>
            From {campaign.completedCount} respondents
          </div>
        </MetricCard>
        <MetricCard accent='default'>
          <MetricLabel>Extrapolated total</MetricLabel>
          <MetricValue unit='tCO2e'>{totalExtrapolated.toFixed(1)}</MetricValue>
          <div className='text-xs text-muted-foreground mt-2'>
            Scaled to {campaign.invitedCount} FTE
          </div>
        </MetricCard>
        <MetricCard accent='amber'>
          <MetricLabel>WFH share</MetricLabel>
          <MetricValue unit='%'>
            {Math.round(((totals.wfhElectricity + totals.wfhGas + totals.wfhOil + totals.wfhSolidFuel + totals.wfhCooling) / totalAll) * 100)}
          </MetricValue>
          <div className='text-xs text-muted-foreground mt-2'>
            vs {Math.round((totals.commuting / totalAll) * 100)}% from commuting
          </div>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Renewable electricity</MetricLabel>
          <MetricValue unit='%'>36</MetricValue>
          <div className='text-xs text-success flex items-center gap-1.5 mt-2'>
            <Leaf size={12} strokeWidth={1.5} />
            weighted by FTE
          </div>
        </MetricCard>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card>
          <CardHeader>
            <CardTitle className='text-base'>WFH breakdown by fuel type</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              All components calculated per PRD §5c. Respondent totals only.
            </p>
          </CardHeader>
          <CardContent>
            <EmissionBar
              label={
                <span className='flex items-center gap-1.5'>
                  <Zap size={12} strokeWidth={1.5} /> Electricity
                </span>
              }
              value={(totals.wfhElectricity / totalAll) * 100}
              displayValue={`${totals.wfhElectricity.toFixed(1)} tCO2e`}
              scope='scope2'
            />
            <EmissionBar
              label={
                <span className='flex items-center gap-1.5'>
                  <Flame size={12} strokeWidth={1.5} /> Natural gas
                </span>
              }
              value={(totals.wfhGas / totalAll) * 100}
              displayValue={`${totals.wfhGas.toFixed(1)} tCO2e`}
              scope='scope1'
            />
            <EmissionBar
              label='Heating oil'
              value={(totals.wfhOil / totalAll) * 100}
              displayValue={`${totals.wfhOil.toFixed(1)} tCO2e`}
              scope='scope1'
            />
            <EmissionBar
              label='Wood / Solid fuel'
              value={(totals.wfhSolidFuel / totalAll) * 100}
              displayValue={`${totals.wfhSolidFuel.toFixed(1)} tCO2e`}
              scope='scope1'
            />
            <EmissionBar
              label={
                <span className='flex items-center gap-1.5'>
                  <Snowflake size={12} strokeWidth={1.5} /> Cooling
                </span>
              }
              value={(totals.wfhCooling / totalAll) * 100}
              displayValue={`${totals.wfhCooling.toFixed(1)} tCO2e`}
              scope='scope2'
            />
            <EmissionBar
              label={
                <span className='flex items-center gap-1.5'>
                  <Car size={12} strokeWidth={1.5} /> Commuting
                </span>
              }
              value={(totals.commuting / totalAll) * 100}
              displayValue={`${totals.commuting.toFixed(1)} tCO2e`}
              scope='scope3'
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Commuting mode split</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              Per-leg distances applied to DEFRA transport factors for {campaign.reportingPeriod}.
            </p>
          </CardHeader>
          <CardContent>
            <Table wrapperClassName='border-0 shadow-none rounded-none'>
              <TableHeader>
                <TableRow>
                  <TableHead>Mode</TableHead>
                  <TableHead className='text-right'>Annual km</TableHead>
                  <TableHead className='text-right'>tCO2e</TableHead>
                  <TableHead className='text-right'>Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {COMMUTING_MODE_SPLIT.map((row) => (
                  <TableRow key={row.mode}>
                    <TableCell>
                      <div className='flex items-center gap-2'>
                        <div className='h-2 rounded-full bg-primary/20 overflow-hidden w-16'>
                          <div
                            className='h-full bg-primary'
                            style={{ width: `${(row.tco2e / maxCommuteTco2e) * 100}%` }}
                          />
                        </div>
                        <span className='text-sm'>{row.mode}</span>
                      </div>
                    </TableCell>
                    <TableCell className='text-right tabular-nums text-sm'>
                      {row.km.toLocaleString()}
                    </TableCell>
                    <TableCell className='text-right tabular-nums text-sm'>{row.tco2e.toFixed(1)}</TableCell>
                    <TableCell className='text-right tabular-nums text-xs text-muted-foreground'>
                      {row.pct}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className='flex-row items-center justify-between space-y-0'>
          <div>
            <CardTitle className='text-base'>Per-location breakdown</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              Respondent totals (raw) and extrapolated totals scaled by each location's response rate.
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <Button size='sm' variant='outline'>
              <FileDown size={14} strokeWidth={1.5} />
              Road-travel CSV
            </Button>
            <Button size='sm'>
              <Download size={14} strokeWidth={1.5} />
              Export all
            </Button>
          </div>
        </CardHeader>
        <Table wrapperClassName='rounded-none border-0 shadow-none'>
          <TableHeader>
            <TableRow>
              <TableHead>Location</TableHead>
              <TableHead className='text-right'>FTE</TableHead>
              <TableHead className='text-right'>Respondents</TableHead>
              <TableHead className='text-right'>Rate</TableHead>
              <TableHead className='text-right'>WFH elec</TableHead>
              <TableHead className='text-right'>WFH gas</TableHead>
              <TableHead className='text-right'>Commute</TableHead>
              <TableHead className='text-right'>Raw total</TableHead>
              <TableHead className='text-right'>Extrapolated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {LOCATION_BREAKDOWN.map((row) => {
              const raw = totalTco2e(row);
              const divisor = row.respondents / row.fte;
              const extrap = Number((raw / divisor).toFixed(1));
              return (
                <TableRow key={row.location}>
                  <TableCell className='font-medium'>{row.location}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm'>{row.fte}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm'>{row.respondents}</TableCell>
                  <TableCell className='text-right'>
                    <Badge
                      variant={row.responseRate >= 60 ? 'success' : 'warning'}
                      size='sm'>
                      {row.responseRate.toFixed(1)}%
                    </Badge>
                  </TableCell>
                  <TableCell className='text-right tabular-nums text-sm'>{row.wfhElectricity.toFixed(1)}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm'>{row.wfhGas.toFixed(1)}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm'>{row.commuting.toFixed(1)}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm font-semibold'>{raw.toFixed(1)}</TableCell>
                  <TableCell className='text-right tabular-nums text-sm font-semibold text-accent'>
                    {extrap.toFixed(1)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default ResultsTab;
